import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { mockData } from '../mock';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/student/login`, loginForm);
      
      if (response.data.success) {
        sessionStorage.setItem('student', JSON.stringify(response.data.student));
        toast({
          title: "Login Successful",
          description: `Welcome, ${response.data.student.name}!`
        });
        navigate('/student/dashboard');
      } else {
        toast({
          title: "Login Failed",
          description: "Invalid email or password",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login Failed",
        description: "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="mb-12">
            <img src={mockData.logo} alt="JurisGo Logo" className="h-16 w-16 object-contain mb-6" />
            <h1 className="text-4xl font-light tracking-tight mb-4">
              Student Portal
            </h1>
            <p className="text-lg text-white/70 font-light leading-relaxed max-w-md">
              Access your recorded sessions, assignments, and class schedules all in one place.
            </p>
          </div>
          <div className="space-y-6 border-t border-white/10 pt-8">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-sm">01</span>
              </div>
              <span className="text-white/70">Watch recorded class sessions</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-sm">02</span>
              </div>
              <span className="text-white/70">Download assignments & resources</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-sm">03</span>
              </div>
              <span className="text-white/70">Join live classes via Google Meet</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        {/* Back Button */}
        <div className="p-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm"
            data-testid="back-to-website-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to website
          </button>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-8 lg:px-16">
          <div className="w-full max-w-sm">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-3 mb-10">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-semibold text-slate-900">JurisGo</span>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                Welcome back
              </h2>
              <p className="text-slate-500 text-sm">
                Enter your credentials to access your learning portal
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" data-testid="student-login-form">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  disabled={isLoading}
                  className="h-12 bg-white border-slate-200 focus:border-slate-900 focus:ring-0 rounded-lg"
                  data-testid="student-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    disabled={isLoading}
                    className="h-12 bg-white border-slate-200 focus:border-slate-900 focus:ring-0 rounded-lg pr-12"
                    data-testid="student-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-all duration-200"
                disabled={isLoading}
                data-testid="student-login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </Button>
            </form>

            <p className="mt-8 text-center text-sm text-slate-500">
              Need help? Contact{' '}
              <a href="mailto:jurisgo.contact@gmail.com" className="text-slate-900 hover:underline">
                support
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 text-center">
          <p className="text-xs text-slate-400">
            JurisGo &copy; {new Date().getFullYear()}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
